import React, { useEffect, useState } from "react";
import {
    Building2, Plus, Loader2, Copy, ExternalLink, Pause, Play, Trash2,
    Sparkles, RefreshCw, X, Zap, DollarSign, Users,
} from "lucide-react";
import api from "../api";
import { toast } from "sonner";
import UsersPanel from "./admin/UsersPanel";

/**
 * Super-Admin Tenants panel — lives inside AdminDashboard as the "Tenants" tab.
 * Lets the platform owner (HeavenlyAI) list / create / suspend / resume / clone /
 * delete restaurants on the HeavenlyEats SaaS platform.
 */
const PLAN_PRICES = { starter: 49, pro: 89 };
const STATUS_BADGE = {
    trial: "bg-blue-100 text-blue-800 border-blue-200",
    active: "bg-leaf-100 text-leaf-800 border-leaf-200",
    past_due: "bg-amber-100 text-amber-800 border-amber-200",
    suspended: "bg-red-100 text-red-800 border-red-200",
    cancelled: "bg-earth-200 text-earth-700 border-earth-300",
};

const TenantsPanel = ({ lang }) => {
    const tt = lang === "es"
        ? {
            title: "Inquilinos (Tenants)",
            subtitle: "Restaurantes en la plataforma HeavenlyEats. Crea, suspende o clona desde aquí.",
            createBtn: "Crear inquilino",
            refresh: "Refrescar",
            empty: "Aún no hay inquilinos. Crea el primero para comenzar.",
            colName: "Restaurante",
            colSlug: "Slug",
            colPlan: "Plan",
            colStatus: "Estado",
            colMenu: "Menú",
            colOrders: "Pedidos",
            colTrialEnd: "Fin de prueba",
            colActions: "Acciones",
            view: "Ver tienda",
            copyLink: "Copiar enlace",
            cloneFrom: "Clonar Delicias",
            suspend: "Suspender",
            resume: "Reactivar",
            delete: "Eliminar",
            deleteConfirm: "¿Eliminar este inquilino y TODOS sus datos? Esta acción no se puede deshacer.",
            cloned: "Datos de Delicias clonados al inquilino",
            suspended: "Inquilino suspendido",
            resumed: "Inquilino reactivado",
            linkCopied: "Enlace copiado",
            // Modal
            mTitle: "Nuevo inquilino",
            mSlug: "Slug (URL): /r/",
            mSlugHelp: "Solo minúsculas, números y guiones. 3-32 caracteres.",
            mName: "Nombre del restaurante",
            mPlan: "Plan",
            mPlanStarter: "Starter — $49/mes",
            mPlanPro: "Pro — $89/mes",
            mTrial: "Días de prueba",
            mEmail: "Email del propietario",
            mWa: "WhatsApp (con código de país)",
            mCancel: "Cancelar",
            mSubmit: "Crear inquilino",
            mCreated: "Inquilino creado",
            mNeed: "Nombre y slug son obligatorios",
            // Activate modal
            activate: "Activar",
            aTitle: "Activar inquilino (cobro manual)",
            aSubtitle: "Cambia el estado a Activo y registra un recibo manual (efectivo, transferencia, Lynk, Tpago, PayPal).",
            aPlan: "Plan tras activación",
            aMethod: "Método de pago",
            aAmount: "Monto cobrado",
            aMonths: "Meses pagados",
            aNote: "Nota (opcional)",
            aSubmit: "Activar y registrar",
            aActivated: "Inquilino activado",
            methodCash: "Efectivo",
            methodBank: "Transferencia bancaria",
            methodLynk: "Lynk",
            methodTpago: "Tpago",
            methodPaypal: "PayPal",
            methodOther: "Otro",
        }
        : {
            title: "Tenants",
            subtitle: "Restaurants on the HeavenlyEats platform. Create, suspend or clone from here.",
            createBtn: "Create tenant",
            refresh: "Refresh",
            empty: "No tenants yet. Create the first one to get started.",
            colName: "Restaurant",
            colSlug: "Slug",
            colPlan: "Plan",
            colStatus: "Status",
            colMenu: "Menu",
            colOrders: "Orders",
            colTrialEnd: "Expires",
            colActions: "Actions",
            view: "View store",
            copyLink: "Copy link",
            cloneFrom: "Clone Delicias",
            suspend: "Suspend",
            resume: "Resume",
            delete: "Delete",
            deleteConfirm: "Delete this tenant and ALL its data? This cannot be undone.",
            cloned: "Delicias data cloned into tenant",
            suspended: "Tenant suspended",
            resumed: "Tenant resumed",
            linkCopied: "Link copied",
            mTitle: "New tenant",
            mSlug: "Slug (URL): /r/",
            mSlugHelp: "Lowercase letters, digits and hyphens only. 3-32 chars.",
            mName: "Restaurant name",
            mPlan: "Plan",
            mPlanStarter: "Starter — $49/mo",
            mPlanPro: "Pro — $89/mo",
            mTrial: "Trial days",
            mEmail: "Owner email",
            mWa: "WhatsApp (with country code)",
            mCancel: "Cancel",
            mSubmit: "Create tenant",
            mCreated: "Tenant created",
            mNeed: "Name and slug are required",
            activate: "Activate",
            aTitle: "Activate tenant (manual receipt)",
            aSubtitle: "Flip status to Active and log a manual receipt (cash, bank transfer, Lynk, Tpago, PayPal).",
            aPlan: "Plan after activation",
            aMethod: "Payment method",
            aAmount: "Amount collected",
            aMonths: "Months paid",
            aNote: "Note (optional)",
            aSubmit: "Activate & record",
            aActivated: "Tenant activated",
            methodCash: "Cash",
            methodBank: "Bank transfer",
            methodLynk: "Lynk",
            methodTpago: "Tpago",
            methodPaypal: "PayPal",
            methodOther: "Other",
        };

    const [tenants, setTenants] = useState([]);
    const [loading, setLoading] = useState(false);
    const [creating, setCreating] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [activating, setActivating] = useState(null);
    const [usersOpenSlug, setUsersOpenSlug] = useState(null);
    const [actForm, setActForm] = useState({
        plan: "starter", payment_method: "cash", amount: 49, months_paid: 1, note: "",
    });
    const [form, setForm] = useState({
        slug: "",
        name: "",
        plan: "starter",
        trial_days: 14,
        owner_email: "",
        whatsapp_number: "",
    });

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get("/super/tenants");
            setTenants(r.data || []);
        } catch {
            toast.error("Error loading tenants");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const openModal = () => {
        setForm({
            slug: "", name: "", plan: "starter", trial_days: 14,
            owner_email: "", whatsapp_number: "",
        });
        setShowModal(true);
    };

    const submitCreate = async (e) => {
        e?.preventDefault();
        if (!form.name.trim() || !form.slug.trim()) {
            toast.error(tt.mNeed);
            return;
        }
        setCreating(true);
        try {
            await api.post("/super/tenants", {
                slug: form.slug.trim().toLowerCase(),
                name: form.name.trim(),
                plan: form.plan,
                trial_days: parseInt(form.trial_days) || 0,
                owner_email: form.owner_email || null,
                whatsapp_number: form.whatsapp_number || null,
            });
            toast.success(tt.mCreated);
            setShowModal(false);
            await load();
        } catch (err) {
            const msg = err?.response?.data?.detail || "Error";
            toast.error(typeof msg === "string" ? msg : "Error");
        } finally {
            setCreating(false);
        }
    };

    const cloneFromDelicias = async (slug) => {
        try {
            await api.post(`/super/tenants/${slug}/clone-from/delicias-original`);
            toast.success(tt.cloned);
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error");
        }
    };

    const suspend = async (slug) => {
        await api.post(`/super/tenants/${slug}/suspend`);
        toast.success(tt.suspended);
        await load();
    };
    const resume = async (slug) => {
        await api.post(`/super/tenants/${slug}/resume`);
        toast.success(tt.resumed);
        await load();
    };
    const remove = async (slug) => {
        if (!window.confirm(tt.deleteConfirm)) return;
        await api.delete(`/super/tenants/${slug}`);
        toast.success("Deleted");
        await load();
    };

    const openActivate = (t) => {
        const plan = t.plan || "starter";
        setActForm({
            plan,
            payment_method: "cash",
            amount: plan === "pro" ? 89 : 49,
            months_paid: 1,
            note: "",
        });
        setActivating(t);
    };

    const submitActivate = async (e) => {
        e?.preventDefault();
        if (!activating) return;
        try {
            await api.post(`/super/tenants/${activating.slug}/activate`, {
                plan: actForm.plan,
                payment_method: actForm.payment_method,
                amount: parseFloat(actForm.amount) || 0,
                months_paid: parseInt(actForm.months_paid) || 1,
                note: actForm.note || null,
            });
            toast.success(tt.aActivated);
            setActivating(null);
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error");
        }
    };
    const copyStoreLink = async (slug) => {
        const url = `${window.location.origin}/r/${slug}`;
        try {
            await navigator.clipboard.writeText(url);
            toast.success(tt.linkCopied);
        } catch {
            toast.error("Clipboard error");
        }
    };

    return (
        <div data-testid="admin-tenants-panel">
            <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
                <div>
                    <h2 className="font-serif font-black text-3xl text-earth-900 flex items-center gap-2">
                        <Building2 className="w-7 h-7 text-terracotta-500" />
                        {tt.title}
                    </h2>
                    <p className="text-earth-600 text-sm mt-1 max-w-xl">{tt.subtitle}</p>
                </div>
                <div className="flex gap-2">
                    <button
                        data-testid="tenants-refresh-btn"
                        onClick={load}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-earth-200 text-earth-700 font-semibold text-sm hover:bg-earth-50"
                    >
                        <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                        {tt.refresh}
                    </button>
                    <button
                        data-testid="tenants-create-btn"
                        onClick={openModal}
                        className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-terracotta-500 hover:bg-terracotta-600 text-white font-semibold text-sm shadow-md"
                    >
                        <Plus className="w-4 h-4" />
                        {tt.createBtn}
                    </button>
                </div>
            </div>

            {/* Tenants table */}
            <div className="bg-white rounded-3xl border border-earth-200 overflow-hidden">
                {loading && tenants.length === 0 ? (
                    <div className="p-12 text-center text-earth-500">
                        <Loader2 className="w-6 h-6 animate-spin inline-block" />
                    </div>
                ) : tenants.length === 0 ? (
                    <div className="p-12 text-center text-earth-500">{tt.empty}</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-earth-50 border-b border-earth-200">
                                <tr className="text-left text-[11px] uppercase tracking-wider text-earth-600 font-bold">
                                    <th className="py-3 px-4">{tt.colName}</th>
                                    <th className="py-3 px-4">{tt.colSlug}</th>
                                    <th className="py-3 px-4">{tt.colPlan}</th>
                                    <th className="py-3 px-4">{tt.colStatus}</th>
                                    <th className="py-3 px-4 text-right">{tt.colMenu}</th>
                                    <th className="py-3 px-4 text-right">{tt.colOrders}</th>
                                    <th className="py-3 px-4">{tt.colTrialEnd}</th>
                                    <th className="py-3 px-4 text-right">{tt.colActions}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {tenants.map((t) => (
                                    <React.Fragment key={t.id}>
                                    <tr
                                        data-testid={`tenant-row-${t.slug}`}
                                        className="border-t border-earth-100 hover:bg-earth-50/50"
                                    >
                                        <td className="py-3 px-4 font-semibold text-earth-900">
                                            {t.name}
                                            {t.owner_email && (
                                                <div className="text-[11px] text-earth-500 font-normal">
                                                    {t.owner_email}
                                                </div>
                                            )}
                                        </td>
                                        <td className="py-3 px-4 font-mono text-xs text-earth-700">
                                            /r/{t.slug}
                                        </td>
                                        <td className="py-3 px-4">
                                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-bold ${
                                                t.plan === "pro" ? "bg-terracotta-500 text-white" : "bg-earth-200 text-earth-800"
                                            }`}>
                                                {t.plan} · ${PLAN_PRICES[t.plan] || "?"}
                                            </span>
                                        </td>
                                        <td className="py-3 px-4">
                                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-bold border ${STATUS_BADGE[t.status] || "bg-earth-100 text-earth-700"}`}>
                                                {t.status}
                                            </span>
                                        </td>
                                        <td className="py-3 px-4 text-right text-earth-700">{t.menu_count ?? 0}</td>
                                        <td className="py-3 px-4 text-right text-earth-700">{t.order_count ?? 0}</td>
                                        <td className="py-3 px-4 text-xs text-earth-500">
                                            {(() => {
                                                const expires =
                                                    t.status === "trial"
                                                        ? t.trial_ends_at
                                                        : t.active_until || t.trial_ends_at;
                                                const d = t.days_until_expiry;
                                                if (!expires) return "—";
                                                const tone =
                                                    d == null ? "bg-earth-100 text-earth-600"
                                                    : d < 0   ? "bg-red-100 text-red-800"
                                                    : d <= 7  ? "bg-red-100 text-red-800"
                                                    : d <= 14 ? "bg-amber-100 text-amber-800"
                                                    :           "bg-leaf-100 text-leaf-800";
                                                const label =
                                                    d == null ? expires.slice(0, 10)
                                                    : d < 0   ? `${expires.slice(0,10)} · ${Math.abs(d)}d ago`
                                                    :           `${expires.slice(0,10)} · ${d}d`;
                                                return (
                                                    <span
                                                        data-testid={`tenant-${t.slug}-expiry-pill`}
                                                        className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold ${tone}`}
                                                    >
                                                        {label}
                                                    </span>
                                                );
                                            })()}
                                        </td>
                                        <td className="py-3 px-4">
                                            <div className="flex items-center justify-end gap-1.5">
                                                <a
                                                    href={`/r/${t.slug}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    data-testid={`tenant-${t.slug}-view-btn`}
                                                    title={tt.view}
                                                    className="p-2 rounded-full hover:bg-earth-100 text-earth-700"
                                                >
                                                    <ExternalLink className="w-4 h-4" />
                                                </a>
                                                <button
                                                    onClick={() => copyStoreLink(t.slug)}
                                                    data-testid={`tenant-${t.slug}-copy-btn`}
                                                    title={tt.copyLink}
                                                    className="p-2 rounded-full hover:bg-earth-100 text-earth-700"
                                                >
                                                    <Copy className="w-4 h-4" />
                                                </button>
                                                {t.menu_count === 0 && t.slug !== "delicias-original" && (
                                                    <button
                                                        onClick={() => cloneFromDelicias(t.slug)}
                                                        data-testid={`tenant-${t.slug}-clone-btn`}
                                                        title={tt.cloneFrom}
                                                        className="p-2 rounded-full hover:bg-leaf-100 text-leaf-700"
                                                    >
                                                        <Sparkles className="w-4 h-4" />
                                                    </button>
                                                )}
                                                {t.status !== "active" && t.slug !== "delicias-original" && (
                                                    <button
                                                        onClick={() => openActivate(t)}
                                                        data-testid={`tenant-${t.slug}-activate-btn`}
                                                        title={tt.activate}
                                                        className="p-2 rounded-full hover:bg-green-100 text-green-700"
                                                    >
                                                        <Zap className="w-4 h-4" />
                                                    </button>
                                                )}
                                                {t.status === "suspended" ? (
                                                    <button
                                                        onClick={() => resume(t.slug)}
                                                        data-testid={`tenant-${t.slug}-resume-btn`}
                                                        title={tt.resume}
                                                        className="p-2 rounded-full hover:bg-leaf-100 text-leaf-700"
                                                    >
                                                        <Play className="w-4 h-4" />
                                                    </button>
                                                ) : (
                                                    <button
                                                        onClick={() => suspend(t.slug)}
                                                        data-testid={`tenant-${t.slug}-suspend-btn`}
                                                        title={tt.suspend}
                                                        className="p-2 rounded-full hover:bg-amber-100 text-amber-700"
                                                    >
                                                        <Pause className="w-4 h-4" />
                                                    </button>
                                                )}
                                                {t.slug !== "delicias-original" && (
                                                    <button
                                                        onClick={() => remove(t.slug)}
                                                        data-testid={`tenant-${t.slug}-delete-btn`}
                                                        title={tt.delete}
                                                        className="p-2 rounded-full hover:bg-red-100 text-red-700"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                    {usersOpenSlug === t.slug && (
                                        <tr key={`${t.slug}-users`} data-testid={`tenant-${t.slug}-users-row`}>
                                            <td colSpan={8} className="bg-earth-50/70 px-4 py-4 border-t border-earth-200">
                                                <div className="text-xs uppercase tracking-wider font-bold text-earth-600 mb-3">
                                                    Usuarios de {t.name || t.slug} / Users
                                                </div>
                                                <UsersPanel tenantSlug={t.slug} mode="super" />
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Activate modal */}
            {activating && (
                <div
                    data-testid="tenant-activate-modal"
                    className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
                    onClick={(e) => { if (e.target === e.currentTarget) setActivating(null); }}
                >
                    <form
                        onSubmit={submitActivate}
                        className="bg-white rounded-3xl border border-earth-200 shadow-2xl w-full max-w-lg p-7"
                    >
                        <div className="flex items-start justify-between mb-3">
                            <div>
                                <h3 className="font-serif font-bold text-2xl text-earth-900 flex items-center gap-2">
                                    <Zap className="w-6 h-6 text-green-600" />
                                    {tt.aTitle}
                                </h3>
                                <div className="text-sm text-earth-600 mt-1">
                                    {activating.name} · <span className="font-mono">/r/{activating.slug}</span>
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={() => setActivating(null)}
                                className="p-2 hover:bg-earth-100 rounded-full"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <p className="text-xs text-earth-500 mb-5">{tt.aSubtitle}</p>

                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-3">
                                <label className="block">
                                    <div className="text-xs font-bold uppercase tracking-wider text-earth-600 mb-1">{tt.aPlan}</div>
                                    <select
                                        data-testid="activate-form-plan"
                                        value={actForm.plan}
                                        onChange={(e) => {
                                            const p = e.target.value;
                                            setActForm({ ...actForm, plan: p, amount: p === "pro" ? 89 : 49 });
                                        }}
                                        className="w-full px-4 py-2.5 rounded-xl border border-earth-200 outline-none"
                                    >
                                        <option value="starter">Starter — $49/mo</option>
                                        <option value="pro">Pro — $89/mo</option>
                                    </select>
                                </label>
                                <label className="block">
                                    <div className="text-xs font-bold uppercase tracking-wider text-earth-600 mb-1">{tt.aMethod}</div>
                                    <select
                                        data-testid="activate-form-method"
                                        value={actForm.payment_method}
                                        onChange={(e) => setActForm({ ...actForm, payment_method: e.target.value })}
                                        className="w-full px-4 py-2.5 rounded-xl border border-earth-200 outline-none"
                                    >
                                        <option value="cash">{tt.methodCash}</option>
                                        <option value="bank_transfer">{tt.methodBank}</option>
                                        <option value="lynk">{tt.methodLynk}</option>
                                        <option value="tpago">{tt.methodTpago}</option>
                                        <option value="paypal">{tt.methodPaypal}</option>
                                        <option value="other">{tt.methodOther}</option>
                                    </select>
                                </label>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <label className="block">
                                    <div className="text-xs font-bold uppercase tracking-wider text-earth-600 mb-1">{tt.aAmount}</div>
                                    <div className="relative">
                                        <DollarSign className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-earth-400" />
                                        <input
                                            data-testid="activate-form-amount"
                                            type="number"
                                            min={0}
                                            step="0.01"
                                            value={actForm.amount}
                                            onChange={(e) => setActForm({ ...actForm, amount: e.target.value })}
                                            className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-earth-200 outline-none"
                                        />
                                    </div>
                                </label>
                                <label className="block">
                                    <div className="text-xs font-bold uppercase tracking-wider text-earth-600 mb-1">{tt.aMonths}</div>
                                    <input
                                        data-testid="activate-form-months"
                                        type="number"
                                        min={1}
                                        max={36}
                                        value={actForm.months_paid}
                                        onChange={(e) => setActForm({ ...actForm, months_paid: e.target.value })}
                                        className="w-full px-4 py-2.5 rounded-xl border border-earth-200 outline-none"
                                    />
                                </label>
                            </div>
                            <label className="block">
                                <div className="text-xs font-bold uppercase tracking-wider text-earth-600 mb-1">{tt.aNote}</div>
                                <input
                                    data-testid="activate-form-note"
                                    value={actForm.note}
                                    onChange={(e) => setActForm({ ...actForm, note: e.target.value })}
                                    className="w-full px-4 py-2.5 rounded-xl border border-earth-200 outline-none"
                                    placeholder="Recibido el 14 de marzo, ref #12345"
                                />
                            </label>
                        </div>

                        <div className="mt-6 flex gap-2 justify-end">
                            <button
                                type="button"
                                onClick={() => setActivating(null)}
                                className="px-5 py-2.5 rounded-full bg-earth-100 hover:bg-earth-200 text-earth-700 font-semibold text-sm"
                            >
                                {tt.mCancel}
                            </button>
                            <button
                                type="submit"
                                data-testid="activate-form-submit-btn"
                                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-green-600 hover:bg-green-700 text-white font-semibold text-sm shadow-md"
                            >
                                <Zap className="w-4 h-4" />
                                {tt.aSubmit}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {/* Create modal */}
            {showModal && (
                <div
                    data-testid="tenant-create-modal"
                    className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
                    onClick={(e) => { if (e.target === e.currentTarget) setShowModal(false); }}
                >
                    <form
                        onSubmit={submitCreate}
                        className="bg-white rounded-3xl border border-earth-200 shadow-2xl w-full max-w-lg p-7"
                    >
                        <div className="flex items-center justify-between mb-5">
                            <h3 className="font-serif font-bold text-2xl text-earth-900">{tt.mTitle}</h3>
                            <button
                                type="button"
                                onClick={() => setShowModal(false)}
                                className="p-2 hover:bg-earth-100 rounded-full"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="space-y-4">
                            <label className="block">
                                <div className="text-xs font-bold uppercase tracking-wider text-earth-600 mb-1">
                                    {tt.mName}
                                </div>
                                <input
                                    data-testid="tenant-form-name"
                                    value={form.name}
                                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                                    className="w-full px-4 py-2.5 rounded-xl border border-earth-200 focus:border-terracotta-400 focus:ring-2 focus:ring-terracotta-200 outline-none"
                                    placeholder="Taqueria Buen Sabor"
                                    required
                                />
                            </label>
                            <label className="block">
                                <div className="text-xs font-bold uppercase tracking-wider text-earth-600 mb-1">
                                    {tt.mSlug}
                                </div>
                                <input
                                    data-testid="tenant-form-slug"
                                    value={form.slug}
                                    onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") })}
                                    className="w-full px-4 py-2.5 rounded-xl border border-earth-200 focus:border-terracotta-400 focus:ring-2 focus:ring-terracotta-200 outline-none font-mono text-sm"
                                    placeholder="taqueria-mx"
                                    required
                                    minLength={3}
                                    maxLength={32}
                                />
                                <div className="text-[11px] text-earth-500 mt-1">{tt.mSlugHelp}</div>
                            </label>
                            <div className="grid grid-cols-2 gap-3">
                                <label className="block">
                                    <div className="text-xs font-bold uppercase tracking-wider text-earth-600 mb-1">
                                        {tt.mPlan}
                                    </div>
                                    <select
                                        data-testid="tenant-form-plan"
                                        value={form.plan}
                                        onChange={(e) => setForm({ ...form, plan: e.target.value })}
                                        className="w-full px-4 py-2.5 rounded-xl border border-earth-200 outline-none"
                                    >
                                        <option value="starter">{tt.mPlanStarter}</option>
                                        <option value="pro">{tt.mPlanPro}</option>
                                    </select>
                                </label>
                                <label className="block">
                                    <div className="text-xs font-bold uppercase tracking-wider text-earth-600 mb-1">
                                        {tt.mTrial}
                                    </div>
                                    <input
                                        data-testid="tenant-form-trial-days"
                                        type="number"
                                        min={0}
                                        max={90}
                                        value={form.trial_days}
                                        onChange={(e) => setForm({ ...form, trial_days: e.target.value })}
                                        className="w-full px-4 py-2.5 rounded-xl border border-earth-200 outline-none"
                                    />
                                </label>
                            </div>
                            <label className="block">
                                <div className="text-xs font-bold uppercase tracking-wider text-earth-600 mb-1">
                                    {tt.mEmail}
                                </div>
                                <input
                                    data-testid="tenant-form-email"
                                    type="email"
                                    value={form.owner_email}
                                    onChange={(e) => setForm({ ...form, owner_email: e.target.value })}
                                    className="w-full px-4 py-2.5 rounded-xl border border-earth-200 outline-none"
                                    placeholder="juan@taqueria.mx"
                                />
                            </label>
                            <label className="block">
                                <div className="text-xs font-bold uppercase tracking-wider text-earth-600 mb-1">
                                    {tt.mWa}
                                </div>
                                <input
                                    data-testid="tenant-form-whatsapp"
                                    value={form.whatsapp_number}
                                    onChange={(e) => setForm({ ...form, whatsapp_number: e.target.value })}
                                    className="w-full px-4 py-2.5 rounded-xl border border-earth-200 outline-none"
                                    placeholder="525511112222"
                                />
                            </label>
                        </div>

                        <div className="mt-6 flex gap-2 justify-end">
                            <button
                                type="button"
                                onClick={() => setShowModal(false)}
                                className="px-5 py-2.5 rounded-full bg-earth-100 hover:bg-earth-200 text-earth-700 font-semibold text-sm"
                            >
                                {tt.mCancel}
                            </button>
                            <button
                                type="submit"
                                data-testid="tenant-form-submit-btn"
                                disabled={creating}
                                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-terracotta-500 hover:bg-terracotta-600 disabled:opacity-60 text-white font-semibold text-sm shadow-md"
                            >
                                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                                {tt.mSubmit}
                            </button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
};

export default TenantsPanel;
